import type { HttpCore } from '../http';
import { ApiError } from '../errors';

/**
 * Types/behavior mirror the `labApi` subset of
 * `frontend/web-ide/src/services/api.ts` that maps onto the mobile-app-plan
 * §1 "labs" inventory. Methods on the original `labApi` whose endpoints fall
 * OUTSIDE that inventory (`getTemplate` non-files variant, `formatCode`,
 * `createPortfolioCheckpoint`'s sub-resource, the legacy `/preferences/*`
 * trio) are intentionally not ported here — see the PR description.
 */

export type ScaffoldingLevel = 'auto' | 'high' | 'medium' | 'low';
export type Region = 'base' | 'us' | 'na' | 'eu' | 'asia' | 'mena' | 'oceania';

export interface LabSubmitResult {
  gradingResult: unknown | null;
  mode?: 'manual';
  status?: 'submitted';
}

export interface SubmissionStatus {
  status: 'grading' | 'done' | 'error' | 'expired';
  gradingResult?: unknown;
  error?: string;
}

export type GpuJobType = 'lora_finetune' | 'qlora_finetune' | 'embedding_finetune';

export interface GpuJobQueued {
  jobId: string;
  status: 'queued';
  queuePosition: number | null;
}

export interface GpuJobStatus {
  status: 'queued' | 'claimed' | 'running' | 'done' | 'error' | 'timeout' | 'cancelled' | 'not_found';
  queuePosition?: number | null;
  progress?: Record<string, unknown> | null;
  result?: Record<string, unknown>;
  error?: string;
}

export interface PortfolioProjectRef {
  visibility: 'private';
  project: { id: string };
  gitStatus: { available: boolean; canCheckpoint: boolean; reason: string | null };
}

type SubmitFiles =
  | Record<string, string>
  | Record<string, { content: string }>
  | Array<{ path: string; content: string }>;

function normalizeFiles(files: SubmitFiles): Array<{ path: string; content: string }> {
  if (Array.isArray(files)) return files;
  return Object.entries(files).map(([path, value]) => ({
    path,
    content: typeof value === 'string' ? value : value.content,
  }));
}

const delay = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

export function createLabsModule(core: HttpCore) {
  const getSubmissionStatus = async (labId: string, submissionId: string): Promise<SubmissionStatus> => {
    try {
      return await core.request<SubmissionStatus>(`/labs/${labId}/submissions/${submissionId}`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        return { status: 'expired' };
      }
      throw err;
    }
  };

  return {
    list: (params?: { published?: boolean; category?: string }) =>
      core.request<unknown>('/labs', { params: params as Record<string, string | number | boolean> }),
    get: (labId: string) => core.request<unknown>(`/labs/${labId}`),
    getTemplateFiles: (labId: string) => core.request<unknown>(`/labs/${labId}/template/files`),
    getStarterFiles: (labId: string) => core.request<unknown>(`/labs/${labId}/starter-files`),
    getSolutionFiles: (labId: string) => core.request<unknown>(`/labs/${labId}/solution-files`),
    getTestCases: (labId: string) => core.request<unknown>(`/labs/${labId}/test-cases`),
    /** No current web-ide caller — typed per the §1 inventory. */
    getTestFiles: (labId: string) => core.request<unknown>(`/labs/${labId}/test-files`),
    getInstructions: (labId: string, options?: { region?: Region }) =>
      core.request<unknown>(`/labs/${labId}/instructions`, { params: options }),
    getScaffoldedFiles: (labId: string, options?: { scaffolding?: ScaffoldingLevel; region?: Region }) =>
      core.request<unknown>(`/labs/${labId}/scaffolded-files`, { params: options }),
    start: (labId: string, environmentId?: string) =>
      core.request<unknown>(`/labs/${labId}/start`, { method: 'POST', body: { environmentId } }),
    /** Lab-manager resolves the approved test runner; the client never supplies a command. */
    run: (labId: string, data: { environmentId: string; artifactReleaseId: string }) =>
      core.request<unknown>(`/labs/${labId}/run`, { method: 'POST', body: data, timeoutMs: 120_000 }),
    getDiff: (labId: string, filename: string, studentCode: string) =>
      core.request<unknown>(`/labs/${labId}/diff`, { method: 'POST', body: { filename, studentCode } }),
    provision: (labId: string) => core.request<unknown>(`/labs/${labId}/provision`, { method: 'POST', timeoutMs: 90_000 }),
    getProgress: (labId?: string) => core.request<unknown>(labId ? `/labs/${labId}/progress` : '/progress'),
    ensurePortfolioProject: (labId: string) =>
      core.request<PortfolioProjectRef>(`/labs/${labId}/portfolio`, { method: 'POST', body: {} }),
    /** No current web-ide caller — typed per the §1 inventory. */
    completeActivity: (labId: string, data: Record<string, unknown> = {}) =>
      core.request<unknown>(`/labs/${labId}/complete-activity`, { method: 'POST', body: data }),
    getSubmissionStatus,
    /**
     * Grading is async: submit returns 202 + a submissionId, and the actual
     * grade (fresh Firecracker microVM boot + sequential pytest execs)
     * exceeds the edge's ~100s limit if awaited inline. POST, then poll until
     * the job resolves — identical contract/behavior to
     * `frontend/web-ide/src/services/portal.ts`'s (moved from `api.ts`)
     * `labApi.submit`, including the sync-path back-compat branch.
     */
    submit: async (labId: string, data: { runAttemptId?: string; files: SubmitFiles }): Promise<LabSubmitResult> => {
      const normalized = normalizeFiles(data.files);
      const response = await core.request<{
        gradingResult?: unknown;
        mode?: 'manual';
        status?: 'submitted';
        submissionId?: string;
      }>(`/labs/${labId}/submit`, {
        method: 'POST',
        body: { files: normalized, runAttemptId: data.runAttemptId },
        timeoutMs: 100_000,
      });

      if (response?.gradingResult) return { gradingResult: response.gradingResult };
      if (response?.mode === 'manual' && response?.status === 'submitted') {
        return { gradingResult: null, mode: 'manual', status: 'submitted' };
      }

      const submissionId = response?.submissionId;
      if (!submissionId) throw new Error('Grader did not return a submission id.');

      const POLL_INTERVAL_MS = 2500;
      const MAX_ATTEMPTS = 140;
      for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
        await delay(POLL_INTERVAL_MS);
        const status = await getSubmissionStatus(labId, submissionId);
        if (status.status === 'done') return { gradingResult: status.gradingResult };
        if (status.status === 'error') throw new Error(status.error || 'Grading failed');
        if (status.status === 'expired') throw new Error('Grading result expired — please resubmit.');
      }
      throw new Error('Grading timed out — please resubmit.');
    },
    submitGpuJob: (labId: string, data: { jobType: GpuJobType; jobSpec: Record<string, unknown> }) =>
      core.request<GpuJobQueued>(`/labs/${labId}/gpu-jobs`, { method: 'POST', body: data }),
    getGpuJobStatus: (labId: string, jobId: string) =>
      core.request<GpuJobStatus>(`/labs/${labId}/gpu-jobs/${jobId}`),
    /** No current web-ide caller — typed per the §1 inventory. */
    getGpuJobArtifact: (labId: string, jobId: string) =>
      core.request<unknown>(`/labs/${labId}/gpu-jobs/${jobId}/artifact`),
  };
}

export type LabsModule = ReturnType<typeof createLabsModule>;
